import { Fragment } from 'react';
import { PortableText } from 'next-sanity';

import {
  PageHomeSchema,
  PageHomeStatsBarItem,
} from '@/sanity/pages/PageHomeTemplate';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { GroqImage } from '@/helpers/Image';
import { cn } from '@/lib/utils';

function StatCard({ item }: { item: PageHomeStatsBarItem }) {
  return (
    <div className="flex h-full flex-1 flex-col justify-between p-[3rem] min-h-[20rem] lg:min-h-[37rem] lg:p-[5.5rem]">
      {item.icon && (
        <div className="relative h-[4rem] w-[4rem] shrink-0 lg:h-[4.5rem] lg:w-[4.5rem]">
          <GroqImage
            src={item.icon}
            alt={item.label ?? ''}
            className="!absolute !inset-0 !h-full !w-full !object-contain"
          />
        </div>
      )}
      <div className="flex flex-col gap-[0.75rem] lg:gap-[1rem]">
        {item.value && (
          <p className="mb-0 font-blacker text-[4rem] italic leading-none tracking-[-0.2rem] text-[#003828] lg:text-[6.375rem] lg:tracking-[-0.32rem]">
            {item.value}
          </p>
        )}
        {item.label && (
          <p className="mb-0 font-geist text-[1.75rem] font-medium leading-[1.5] tracking-[-0.09rem] text-black/70 lg:text-[2.25rem] lg:tracking-[-0.11rem]">
            {item.label}
          </p>
        )}
      </div>
    </div>
  );
}

export default function StatsBarSection({ data }: { data: PageHomeSchema }) {
  const items = data.statsBarItems ?? [];

  if (!data.statsBarTitle && !items.length) return null;

  return (
    <section className="w-full bg-[#f4f5f5]">
      {/* Header */}
      <div className="mx-auto flex w-[min(86rem,calc(100%-4rem))] flex-col items-center gap-[2rem] pb-[6rem]  pt-[10rem] text-center">
        {data.statsBarChip && (
          <p className="mb-0 font-blacker text-[2rem] italic leading-[1.5] text-black">
            {data.statsBarChip}
          </p>
        )}
        {data.statsBarTitle && (
          <div className="richtext flex w-full flex-col gap-[2rem] prose-em:font-blacker prose-em:italic prose-em:text-[#003828] prose-p:max-w-[60rem] prose-p:text-[2rem] prose-p:leading-[1.5] prose-p:text-black/60">
            <PortableText
              value={data.statsBarTitle}
              components={richTextComponent}
            />
          </div>
        )}
      </div>

      {/* Stat cards */}
      <div className="mx-auto w-[min(147.25rem,calc(100%-4rem))] pb-[8rem] lg:pb-[15rem]">
        {/* Mobile: 2×2 grid */}
        <div className="grid grid-cols-2 grid-rows-2 lg:hidden">
          {items.map((item, i) => (
            <div
              key={item._key}
              className={cn(
                'flex h-full flex-col',
                i % 2 === 0 && 'border-r border-[rgba(0,27,20,0.1)]',
                i < items.length - 2 && 'border-b border-[rgba(0,27,20,0.1)]',
              )}
            >
              <StatCard item={item} />
            </div>
          ))}
        </div>

        {/* Desktop: flex row with vertical dividers */}
        <div className="hidden  items-stretch lg:flex">
          {items.map((item, i) => (
            <Fragment key={item._key}>
              {i > 0 && (
                <div className="w-px shrink-0 self-stretch bg-[rgba(0,27,20,0.1)]" />
              )}
              <StatCard item={item} />
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}
